// The one HTTP client. Every call the UI makes goes through here so the
// mock (VITE_MDJ_MOCK=1) and the real API share a contract — the UI cannot
// tell them apart, which is what lets the screen be built and rendered
// before the backend exists.
import {
  Clip, ClipRequest, Player, PlayerCreate, PlayerUpdate, youtubeVideoId,
} from "@mdj/shared";
import { getAccessToken } from "./auth";
import { config } from "./config";

export type Probe = { videoId: string; title: string | null; durationSeconds: number; thumbnail: string };

export interface Api {
  probe(url: string): Promise<Probe>;
  createClip(req: ClipRequest): Promise<Clip>;
  getClip(id: string): Promise<Clip>;
  clipAudioUrl(clip: Clip | { id: string }): Promise<string>; // object URL — fetched with the bearer, <audio> can't send one
  listPlayers(): Promise<Player[]>;
  createPlayer(p: PlayerCreate): Promise<Player>;
  updatePlayer(id: string, p: PlayerUpdate): Promise<Player>;
  deletePlayer(id: string): Promise<void>;
  reorderPlayers(ids: string[]): Promise<Player[]>;
  assignWalkup(playerId: string, clipId: string | null): Promise<Player>;
  uploadAnnouncement(playerId: string, blob: Blob): Promise<Player>;
  announcementUrl(playerId: string): Promise<string>; // object URL, fetched with the bearer
  deleteAnnouncement(playerId: string): Promise<Player>;
}

const BASE = () => config().apiBase;

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await getAccessToken();
  const headers = new Headers(init.headers);
  if (token) headers.set("authorization", `Bearer ${token}`);
  if (init.body && !(init.body instanceof FormData) && !headers.has("content-type")) {
    headers.set("content-type", "application/json");
  }
  const res = await fetch(`${BASE()}${path}`, { ...init, headers });
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    try { const j = await res.json(); if (j?.error) msg = String(j.error); } catch { /* keep default */ }
    throw new Error(msg);
  }
  return (await res.json()) as T;
}

const real: Api = {
  probe: (url) => call("/clips/probe", { method: "POST", body: JSON.stringify({ url }) }),
  createClip: (req) => call("/clips", { method: "POST", body: JSON.stringify(req) }),
  getClip: (id) => call(`/clips/${id}`),
  clipAudioUrl: async (clip) => {
    const token = await getAccessToken();
    const res = await fetch(`${BASE()}/clips/${clip.id}/audio`, { headers: token ? { authorization: `Bearer ${token}` } : {} });
    if (!res.ok) throw new Error(`audio ${res.status}`);
    return URL.createObjectURL(await res.blob());
  },
  listPlayers: () => call("/players"),
  createPlayer: (p) => call("/players", { method: "POST", body: JSON.stringify(p) }),
  updatePlayer: (id, p) => call(`/players/${id}`, { method: "PATCH", body: JSON.stringify(p) }),
  deletePlayer: async (id) => {
    const token = await getAccessToken();
    const res = await fetch(`${BASE()}/players/${id}`, { method: "DELETE", headers: token ? { authorization: `Bearer ${token}` } : {} });
    if (!res.ok) throw new Error(`${res.status}`);
  },
  reorderPlayers: (ids) => call("/players/order", { method: "PUT", body: JSON.stringify({ ids }) }),
  assignWalkup: (playerId, clipId) =>
    call(`/players/${playerId}/walkup`, { method: "PUT", body: JSON.stringify({ walkupClipId: clipId }) }),
  uploadAnnouncement: (playerId, blob) => {
    const fd = new FormData();
    const ext = blob.type.includes("mp4") ? "m4a" : blob.type.includes("ogg") ? "ogg" : "webm";
    fd.set("file", blob, `announcement.${ext}`);
    return call(`/players/${playerId}/announcement`, { method: "POST", body: fd });
  },
  deleteAnnouncement: (playerId) => call(`/players/${playerId}/announcement`, { method: "DELETE" }),
  announcementUrl: async (playerId) => {
    const token = await getAccessToken();
    const res = await fetch(`${BASE()}/players/${playerId}/announcement`, { headers: token ? { authorization: `Bearer ${token}` } : {} });
    if (!res.ok) throw new Error(`announcement ${res.status}`);
    return URL.createObjectURL(await res.blob());
  },
};

// ---- mock -----------------------------------------------------------------
// Placeholder audio: a 3-second synthesized "walk-up hit" so the preview
// control has something real to play. Generated lazily, once.
// One blob, a FRESH object URL per call: callers revoke the URLs they own
// (StrictMode double-runs effects, so a shared URL would get revoked under
// the next consumer).
let placeholderBlob: Blob | null = null;
function placeholderAudio(): string {
  if (placeholderBlob) return URL.createObjectURL(placeholderBlob);
  const rate = 22050, secs = 20, n = rate * secs;
  const buf = new ArrayBuffer(44 + n * 2), v = new DataView(buf);
  const w = (o: number, s: string) => { for (let i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i)); };
  w(0, "RIFF"); v.setUint32(4, 36 + n * 2, true); w(8, "WAVE"); w(12, "fmt ");
  v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 1, true);
  v.setUint32(24, rate, true); v.setUint32(28, rate * 2, true); v.setUint16(32, 2, true); v.setUint16(34, 16, true);
  w(36, "data"); v.setUint32(40, n * 2, true);
  for (let i = 0; i < n; i++) {
    const t = i / rate, env = Math.exp(-t * 1.2);
    const s = Math.sin(2 * Math.PI * 110 * t) * 0.5 + Math.sin(2 * Math.PI * 220 * t) * 0.3 + Math.sin(2 * Math.PI * 330 * t) * 0.15;
    v.setInt16(44 + i * 2, Math.max(-1, Math.min(1, s * env)) * 32767, true);
  }
  placeholderBlob = new Blob([buf], { type: "audio/wav" });
  return URL.createObjectURL(placeholderBlob);
}

const now = () => new Date().toISOString();
// crypto.randomUUID only exists in secure contexts; a dev server reached over
// plain http from another device (the puppet browser) is not one.
const uuid = () =>
  typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (c) =>
        (Number(c) ^ (crypto.getRandomValues(new Uint8Array(1))[0]! & (15 >> (Number(c) / 4)))).toString(16));
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

const mockClips = new Map<string, Clip>();
const mockPlayers: Player[] = [
  { id: uuid(), ownerSub: "mock", name: "Mateo Reyes", number: "7", walkupClipId: null, announcementKey: null, sortOrder: 0, createdAt: now(), updatedAt: now() },
  { id: uuid(), ownerSub: "mock", name: "Jaxon Lee", number: "23", walkupClipId: null, announcementKey: "x", sortOrder: 1, createdAt: now(), updatedAt: now() },
  { id: uuid(), ownerSub: "mock", name: "Eli Thompson", number: "12", walkupClipId: null, announcementKey: null, sortOrder: 2, createdAt: now(), updatedAt: now() },
];

const mock: Api = {
  async probe(url) {
    await sleep(500);
    const videoId = youtubeVideoId(url);
    if (!videoId) throw new Error("Not a YouTube link");
    return { videoId, title: "Placeholder title — mock probe", durationSeconds: 213, thumbnail: `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg` };
  },
  async createClip(req) {
    const id = uuid();
    const clip: Clip = {
      id, ownerSub: "mock", sourceUrl: req.url, videoId: youtubeVideoId(req.url) ?? "", title: "Placeholder title",
      window: req.window, status: "queued", objectKey: null, durationSeconds: null, error: null, createdAt: now(), updatedAt: now(),
    };
    mockClips.set(id, clip);
    // Walk the status machine on a timer so polling in the UI is exercised.
    setTimeout(() => mockClips.set(id, { ...clip, status: "extracting", updatedAt: now() }), 800);
    setTimeout(() => mockClips.set(id, { ...clip, status: "ready", objectKey: `clips/${id}.m4a`, durationSeconds: req.window.stop - req.window.start, updatedAt: now() }), 2600);
    return clip;
  },
  async getClip(id) { await sleep(150); const c = mockClips.get(id); if (!c) throw new Error("not found"); return c; },
  clipAudioUrl: async () => placeholderAudio(),
  async listPlayers() { await sleep(200); return [...mockPlayers]; },
  async createPlayer(p) {
    const player: Player = { id: uuid(), ownerSub: "mock", ...p, walkupClipId: null, announcementKey: null, sortOrder: mockPlayers.length, createdAt: now(), updatedAt: now() };
    mockPlayers.push(player); return player;
  },
  async updatePlayer(id, p) {
    const x = mockPlayers.find((y) => y.id === id); if (!x) throw new Error("not found");
    Object.assign(x, p, { updatedAt: now() }); await sleep(150); return { ...x };
  },
  async deletePlayer(id) { const i = mockPlayers.findIndex((y) => y.id === id); if (i >= 0) mockPlayers.splice(i, 1); await sleep(150); },
  async reorderPlayers(ids) {
    mockPlayers.sort((a, b) => ids.indexOf(a.id) - ids.indexOf(b.id));
    mockPlayers.forEach((p, i) => { p.sortOrder = i; }); await sleep(150); return [...mockPlayers];
  },
  async assignWalkup(playerId, clipId) {
    const p = mockPlayers.find((x) => x.id === playerId); if (!p) throw new Error("not found");
    p.walkupClipId = clipId; p.updatedAt = now(); await sleep(300); return { ...p };
  },
  async uploadAnnouncement(playerId, blob) {
    const p = mockPlayers.find((x) => x.id === playerId); if (!p) throw new Error("not found");
    p.announcementKey = `announce/${playerId}.webm`; mockAnnouncements.set(playerId, blob); await sleep(300); return { ...p };
  },
  async deleteAnnouncement(playerId) {
    const p = mockPlayers.find((x) => x.id === playerId); if (!p) throw new Error("not found");
    p.announcementKey = null; mockAnnouncements.delete(playerId); await sleep(200); return { ...p };
  },
  async announcementUrl(playerId) {
    const b = mockAnnouncements.get(playerId);
    // A simulated take (no microphone in the rig) is not decodable audio: play the placeholder.
    return b && b.size > 4096 ? URL.createObjectURL(b) : placeholderAudio();
  },
};
const mockAnnouncements = new Map<string, Blob>();

export const MOCK = (): boolean => config().mock;
export const api: Api = new Proxy({} as Api, { get: (_t, k) => (MOCK() ? mock : real)[k as keyof Api] });
